/**
 * The brand, in one place, because it was in three and they drifted.
 *
 * The agency app, the public listing site and the exported assets each had their own idea of the
 * wordmark — one drew the separator as a disc with a pip in it, one typed a middle dot, and one
 * loaded a webfont the others did not have. They looked like two different products, which is the
 * one thing a brand exists to prevent.
 *
 * So the colours, the type and the wordmark's own CSS live here, and everything else reads them:
 *
 * - `apps/listings` imports `WORDMARK_CSS` and the stacks directly into its stylesheet.
 * - `apps/web` keeps its Tailwind theme, and `tools/sync-brand.mjs --check` fails the build when
 *   its copy of these rules stops matching this file.
 * - `assets/brand/build.mjs` writes the exported SVGs and PNGs from the same numbers.
 *
 * **The two surfaces are set in different faces, and that is the one deliberate difference.** The
 * agency app uses the system stack: it is furniture somebody sits in front of all day, and it
 * should start instantly and look native to the machine. The public site is set in Poppins,
 * because it is a shop window seen once by somebody who has never heard of us. Everything else —
 * the mark, the greens, the two radii, the construction of the wordmark — is identical.
 */

/** The two greens the mark is drawn in, and the dark ground they sit on. */
export const BRAND_COLOURS = {
  /** The rail, the hero, the footer. */
  ink: "#0B3B2E",
  inkDeep: "#07281F",
  /** Buttons and links. */
  primary: "#12805C",
  primaryHover: "#0F6A4D",
  /** The lighter of the two greens in the mark, and the disc the separator sits on. */
  light: "#6EE7B7",
  mid: "#2FBF8A",
  /** The one warm colour, used on the public site for what is verified. */
  brass: "#C9922B",
} as const;

/**
 * The type.
 *
 * `display` is the wordmark and the headings; `body` is everything else. They are nearly the same
 * stack on purpose — the difference between a heading and a paragraph here is weight and tracking,
 * not a second typeface, which is what keeps the two apps looking like one product.
 */
export const BRAND_FONTS = {
  display: `"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif`,
  body: `ui-sans-serif, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
  mono: `ui-monospace, "Cascadia Mono", "Segoe UI Mono", Menlo, Consolas, monospace`,
  /**
   * The public site is set in Poppins.
   *
   * The agency app is furniture somebody sits in front of all day, and it uses the system face so
   * it starts instantly and looks native. The listing site is a shop window seen once by somebody
   * who has never heard of us, and there the face is part of the impression — so it is a real
   * typeface, with the system stack behind it for the first paint.
   */
  listing: `Poppins, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif`,
} as const;

/**
 * Where Poppins came from, once.
 *
 * **Nothing loads this any more, and nothing should.** A stylesheet link to another origin blocks
 * the first paint — two DNS lookups, two handshakes and a round trip — and it measured 750ms of a
 * 1.6 second block on a phone. The listing site serves the four weights itself out of
 * `apps/listings/public/fonts/`, declared in its own inlined stylesheet. Kept here only to name
 * the source of the files and the release they came from: Poppins v24, latin and latin-ext.
 */
export const LISTING_FONT_SOURCE =
  "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap";

/**
 * Two radii, on purpose — and the small one is deliberate.
 *
 * Controls are 3px: nearly square, which is what makes this product look like a tool rather than a
 * consumer app. Surfaces are 12px. Every rounded thing in either app is one of these two, so a
 * button on the listing site and a button in the portal are the same shape.
 */
export const BRAND_RADII = { control: "3px", surface: "12px", pill: "999px" } as const;

/**
 * The wordmark's own rules, as plain CSS.
 *
 * Kept as a string rather than as Tailwind classes or a component, because it has to be usable by
 * a React app, by a server-rendered page and by an exported asset without any of them depending on
 * the others' framework.
 *
 * The numbers are settled, not guessed — see the note on the disc below.
 */
export const WORDMARK_CSS = `
.brand-word {
  font-family: var(--brand-word-font, ${BRAND_FONTS.display});
  /* 700, not 800: this face has Bold and Black and nothing between, so 800 rounds up to Black
     and nothing visibly changes. */
  font-weight: 700;
  letter-spacing: -0.03em;
  display: inline-flex;
  align-items: baseline;
  white-space: nowrap;
}

/**
 * The dot between the two halves: a disc of the lighter brand green with the ink dot on it.
 *
 * It reads as the dot of a domain without spelling one out, and it stops "Rentor" and "NG" running
 * together into one unfamiliar word. Drawn rather than typed, because a full stop between two
 * words at this weight disappears.
 *
 * A third smaller than it first was, settled over three passes. At 0.54em the disc was the
 * x-height of the lettering, and a round green shape the size of a letter is read as one — the
 * name came across as "Rentoro NG". 0.378em is where it stopped.
 */
.brand-dot {
  display: inline-block;
  width: 0.378em;
  height: 0.378em;
  /* Tight: the disc is punctuation inside one word, not a separator between two. */
  margin: 0 0.08em;
  /* Baseline-aligned text would hang the disc below the letters; this lifts it to the middle of
     the x-height, where a full stop between two capitals would sit. */
  transform: translateY(-0.041em);
}

.brand-dot-disc { fill: ${BRAND_COLOURS.light}; }

/* The dark green reads on the light disc on either ground — the disc is what changes with the
   background, not the pip inside it. */
.brand-dot-pip { fill: ${BRAND_COLOURS.ink}; }
`;

/**
 * The separator, as markup.
 *
 * Concentric circles on a 24-unit grid: two nested boxes centred by percentages resolve to
 * fractional pixels, and at rail size the inner dot came out about 3.1px wide and landed
 * off-centre once the browser had rounded it. The ratio is the exported asset's — 8 units of dot
 * to 19 of disc.
 */
export const WORDMARK_DOT_SVG =
  `<svg class="brand-dot" viewBox="0 0 24 24" aria-hidden="true" focusable="false">` +
  `<circle cx="12" cy="12" r="12" class="brand-dot-disc"/>` +
  `<circle cx="12" cy="12" r="5.05" class="brand-dot-pip"/>` +
  `</svg>`;

/** The two halves either side of it. Never hard-code these — a rename should be one edit. */
export const BRAND_NAME = { first: "Rentor", second: "NG", full: "RentorNG" } as const;

export const BRAND_TAGLINE = "Properties with ease.";

/* ------------------------------------------------------------------ the waiting animation */

/**
 * The loader: **the street lights up.**
 *
 * The mark is a street of three buildings — a short block, a house with a keyhole, a taller
 * block. The waiting animation is that same street, unchanged, with a light travelling along it:
 * the left block wakes, then the house, then the tall block, and round again. Nothing moves off
 * the ground line, nothing spins, no shape is ever absent, so at every moment of the loop the
 * thing on screen is still the logo. That is the whole idea — a loader that is the mark being
 * itself rather than a circle borrowed from somewhere else.
 *
 * It says two things at once, which is why it was chosen over the other four sketches:
 *
 *  - **A portfolio, unit by unit.** A light going on in one building and then the next is what a
 *    street of managed properties does in the evening. The product's promise is "every unit,
 *    every payment, every renewal" — one at a time, none skipped.
 *  - **The keyhole is the middle beat.** The house's keyhole is not drawn while the house is
 *    resting; it is cut through at the moment the house lights. So the centre of every cycle is
 *    the handover, which is the one image the mark was built around.
 *
 * **Two scales of expression, from one animation.** At 20px beside a button the windows are under
 * three pixels wide and nobody can see them; what reads there is the brightness travelling across
 * three big silhouettes. At 96px on a whole-page load the same event also cuts the windows and the
 * keyhole through. The small size is not a different asset and not a simplification — it is the
 * same drawing losing detail the way any drawing does.
 *
 * **Why brightness and not movement.** This is watched while somebody is stuck, sometimes for
 * eight seconds. Anything that travels, bounces or rotates is asking to be looked at; a light
 * coming on is not. The one piece of movement left is the roof's 3% rise over its own base line as
 * a building wakes — set the lift knob to 1 and it is gone, and nothing else changes.
 *
 * **Opacity, not a paler green, is what does the dimming** — because the loader has to sit on the
 * agency app's off-white, the listing site's paper and the console's near-black. A tint mixed
 * towards white dies on paper and a tint mixed towards black dies on the console; letting the
 * ground itself show through is the only dimming that behaves the same on both.
 */

/** The three buildings, in the order the light travels: left block, house, right block. */
export const LOADER_BUILDINGS = [
  {
    fill: BRAND_COLOURS.mid,
    stroke: null,
    /** The mark's own path, knockouts and all. `assets/logo/rentorng-mark.svg` is the original. */
    d: "M19 296 135 232v221H19zM45 300h64v58H45zM45 372h64v58H45z",
    /** The knockouts again, drawn over the top in the building's own colour so they vanish. */
    lights: ["M45 300h64v58H45z", "M45 372h64v58H45z"],
  },
  {
    fill: BRAND_COLOURS.light,
    stroke: BRAND_COLOURS.mid,
    d: "M148 96 364 26v422H148zM256 196a54 54 0 0 0-22 103l-15 77h74l-15-77a54 54 0 0 0-22-103z",
    lights: ["M256 196a54 54 0 0 0-22 103l-15 77h74l-15-77a54 54 0 0 0-22-103z"],
  },
  {
    fill: BRAND_COLOURS.mid,
    stroke: null,
    d: "M377 148 493 212v241H377zM403 232h64v72h-64zM403 344h64v72h-64z",
    lights: ["M403 232h64v72h-64z", "M403 344h64v72h-64z"],
  },
] as const;

/**
 * The one line the whole thing hangs off: every building stands on y=453 in the 512 grid.
 *
 * The lift scales about this line rather than about each shape's own box, so the three roofs rise
 * and the three bases do not — the house's fill actually stops at 448 and reaches 453 only through
 * its hairline, and scaling it about its own box would slide it half a unit off the street.
 */
export const LOADER_GROUND = 453;

/**
 * The loader's rules, as plain CSS, for the same reason `WORDMARK_CSS` is a string: a React app, a
 * server-rendered page and a standalone `.svg` file all need them and none of them can import the
 * others' framework.
 *
 * Five knobs, all settable on the wrapper or on any ancestor:
 *
 * | Property | Default | What it does |
 * |---|---|---|
 * | `--ldr-size` | `48px` | the square the mark is drawn in |
 * | `--ldr-period` | `1800ms` | one trip along the street |
 * | `--ldr-rest` | `0.3` | how dim a building is between its turns |
 * | `--ldr-lift` | `1.03` | the roof's rise; `1` removes the movement entirely |
 * | `--ldr-ease` | `cubic-bezier(.4,0,.2,1)` | how a light comes on |
 *
 * **The base state is the finished mark at full strength, deliberately.** Both the dimming and the
 * covered windows live inside the keyframes, so a stylesheet that kills animation wholesale — the
 * listing site does exactly that under `prefers-reduced-motion` — is left with the logo, correct
 * and static, rather than a dim silhouette with no windows.
 */
export const LOADER_CSS = `
.rentorng-loader {
  --ldr-size: 48px;
  --ldr-period: 1800ms;
  --ldr-rest: 0.3;
  --ldr-lift: 1.03;
  --ldr-ease: cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: var(--ldr-size);
  height: var(--ldr-size);
  line-height: 0;
}

/* Small enough to sit on a button's line, so it is quicker than the other two — a slow loop on a
   20px mark reads as a stall rather than as work. */
.rentorng-loader-sm { --ldr-size: 20px; --ldr-period: 1500ms; }
.rentorng-loader-md { --ldr-size: 48px; }
/* And slower at page size: the bigger the travel, the calmer it has to be. */
.rentorng-loader-lg { --ldr-size: 96px; --ldr-period: 2100ms; }

.rentorng-loader svg {
  display: block;
  width: 100%;
  height: 100%;
}

/*
  A building, and the two things that happen to it on its turn.

  The delay is held as a custom property rather than written straight into the animation-delay,
  because the covers inside the group need the same number, and custom properties inherit where
  animation-delay does not.
*/
.rentorng-loader-b {
  opacity: 1;
  transform-box: view-box;
  transform-origin: 256px ${LOADER_GROUND}px;
  animation: rentorng-loader-wake var(--ldr-period) var(--ldr-ease) infinite backwards;
  animation-delay: var(--ldr-delay, 0ms);
}
.rentorng-loader-b1 { --ldr-delay: 0ms; }
.rentorng-loader-b2 { --ldr-delay: calc(var(--ldr-period) / 3); }
.rentorng-loader-b3 { --ldr-delay: calc(var(--ldr-period) / 3 * 2); }

/*
  The cover over a window or the keyhole: the same shape as the knockout under it, painted in the
  building's own colour, so at rest the building is a plain silhouette and its windows appear only
  when the light comes on. Stroked as well as filled because the house's keyhole carries a hairline
  in the neighbours' green, and half of a stroke stands outside the shape it is drawn on.
*/
.rentorng-loader-shut {
  opacity: 1;
  animation: rentorng-loader-shutter var(--ldr-period) var(--ldr-ease) infinite backwards;
  animation-delay: var(--ldr-delay, 0ms);
}

@keyframes rentorng-loader-wake {
  0%, 100% { opacity: var(--ldr-rest); transform: scaleY(1); }
  14%, 34% { opacity: 1; transform: scaleY(var(--ldr-lift)); }
  56% { opacity: var(--ldr-rest); transform: scaleY(1); }
}

@keyframes rentorng-loader-shutter {
  0%, 100% { opacity: 1; }
  14%, 34% { opacity: 0; }
  56% { opacity: 1; }
}

/* The label is for a screen reader only, and is never the thing that makes the box a box. */
.rentorng-loader-label {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  white-space: nowrap;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
}

/*
  Somebody who has asked for less movement gets none of it.

  Not nothing at all, though: a spinner that stops dead is a page that looks broken. The whole mark
  breathes once every four seconds instead — slow enough that it is not seen unless it is looked
  for, and present enough to say the wait is still a wait. A stylesheet that bans animation outright
  leaves the mark static and correct, which is the other acceptable answer.
*/
@media (prefers-reduced-motion: reduce) {
  .rentorng-loader-b { animation: none; opacity: 1; transform: none; }
  .rentorng-loader-shut { animation: none; opacity: 0; }
  .rentorng-loader svg { animation: rentorng-loader-breathe 4s ease-in-out infinite; }
}

@keyframes rentorng-loader-breathe {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}
`;

/**
 * The drawing, as markup, for the two surfaces that are not React.
 *
 * `apps/web` builds the same tree out of `LOADER_BUILDINGS` in JSX rather than using this string —
 * the geometry is shared, the syntax is not.
 */
export const LOADER_SVG = [
  `<svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">`,
  ...LOADER_BUILDINGS.map(
    (b, i) =>
      `  <g class="rentorng-loader-b rentorng-loader-b${i + 1}">\n` +
      `    <path fill="${b.fill}" fill-rule="evenodd"` +
      (b.stroke ? ` stroke="${b.stroke}" stroke-width="10" stroke-linejoin="round"` : "") +
      ` d="${b.d}"/>\n` +
      b.lights
        .map(
          (d) =>
            `    <path class="rentorng-loader-shut" fill="${b.fill}" stroke="${b.fill}"` +
            ` stroke-width="10" stroke-linejoin="round" d="${d}"/>`,
        )
        .join("\n") +
      `\n  </g>`,
  ),
  `</svg>`,
].join("\n");

/** The default thing a screen reader should say while the mark is turning. */
export const LOADER_LABEL = "Loading";

/**
 * The whole control, wrapper and label included, for a server-rendered page.
 *
 * `role="status"` rather than `role="img"`: this is not a picture somebody is looking at, it is a
 * report that something is happening, and it should be announced when it appears without stealing
 * the focus. The drawing itself is hidden from the tree so the announcement is one thing, not two.
 */
export const loaderMarkup = (size: "sm" | "md" | "lg" = "md", label: string = LOADER_LABEL) =>
  `<span class="rentorng-loader rentorng-loader-${size}" role="status">` +
  LOADER_SVG +
  `<span class="rentorng-loader-label">${label}</span>` +
  `</span>`;
